
import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';

interface ProjectAvatar {
  image: string | null;
  name: string | null;
  onClick: (() => void) | null;
}

interface AppContextType {
  darkMode: boolean;
  setDarkMode: (value: boolean) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (value: boolean) => void;
  projects: any[];
  setProjects: (projects: any[]) => void;
  documents: any[];
  setDocuments: (documents: any[]) => void;
  sidebarRefreshTrigger: number;
  triggerSidebarRefresh: () => void;
  projectAvatar: ProjectAvatar;
  setProjectAvatar: (avatar: ProjectAvatar) => void;
  clearProjectAvatar: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
  const [projectAvatar, setProjectAvatar] = useState<ProjectAvatar>({ image: null, name: null, onClick: null });

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const triggerSidebarRefresh = () => {
    setSidebarRefreshTrigger(prev => prev + 1);
  };

  const clearProjectAvatar = useCallback(() => {
    setProjectAvatar({ image: null, name: null, onClick: null });
  }, []);

  const value = {
    darkMode, setDarkMode,
    sidebarCollapsed, setSidebarCollapsed,
    projects, setProjects,
    documents, setDocuments,
    sidebarRefreshTrigger,
    triggerSidebarRefresh,
    projectAvatar,
    setProjectAvatar,
    clearProjectAvatar
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
